import * as React from 'react';
import type { Placement } from '@mentimeter/presentation-state';
import { Box } from '@mentimeter/ragnar-ui/box';
import { usePace } from '../misc/pace';

const SWIPE_RIGHT = 'swipe-right';
const SWIPE_LEFT = 'swipe-left';
const THRESHOLD = 50;

interface GesturesT {
  children: React.ReactNode;
  next: (placement: Placement) => void;
  previous: (placement: Placement) => void;
}

const C = React.memo<GesturesT>(({ next, previous, children }: GesturesT) => {
  const startX = React.useRef(0);
  const currentX = React.useRef(0);

  const updateSlide = React.useCallback(
    (direction: typeof SWIPE_RIGHT | typeof SWIPE_LEFT) => {
      switch (direction) {
        case SWIPE_LEFT:
          next('Gesture swipe');
          break;
        case SWIPE_RIGHT:
          previous('Gesture swipe');
          break;
        default:
          break;
      }
    },
    [next, previous],
  );

  const touchStart = React.useCallback((e: React.TouchEvent<HTMLElement>) => {
    const touch = e.touches[0];
    if (touch) {
      const { clientX } = touch;
      startX.current = clientX;
      currentX.current = clientX;
    }
  }, []);

  const touchMove = React.useCallback((e: React.TouchEvent<HTMLElement>) => {
    const touch = e.touches[0];
    if (touch) {
      const { clientX } = touch;
      currentX.current = clientX;
    }
  }, []);

  const touchEnd = React.useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    Math.abs(startX.current - currentX.current) > THRESHOLD &&
      (startX.current < currentX.current
        ? updateSlide(SWIPE_RIGHT)
        : updateSlide(SWIPE_LEFT));
  }, [updateSlide]);

  return (
    <Box
      width="100%"
      height="100%"
      position="absolute"
      onTouchStart={touchStart}
      onTouchMove={touchMove}
      onTouchEnd={touchEnd}
    >
      {children}
    </Box>
  );
});

/**
 * This wrapper component adds touch events to the presentation view
 * to enable users to swipe between steps/slides on mobile
 */
const Gestures = ({ children }: { children: React.ReactNode }) => {
  const { state, actions } = usePace();
  if (!state || !actions) {
    throw new Error(
      'tried to render PresentationActions without providing pace data',
    );
  }
  return (
    <C next={actions.next} previous={actions.previous}>
      {children}
    </C>
  );
};

export default Gestures;
